<template>
  <Modal
    ref="errorModal"
    hide-close
    @cancel="close"
    class="theme-default scheme-box !max-w-none w-[40vw]"
  >
    <!-- Contenido -->
    <div class="py-8 px-12">
      <h3 class="text-3xl font-bold mb-6 text-left" :class="titleClasses">
        {{ notification.title }}
      </h3>
      <p class="text-xl text-left leading-relaxed" :class="messageClasses">
        {{ notification.message }}
      </p>

      <details v-if="hasDetails" open class="mt-6 text-sm">
        <summary class="cursor-pointer text-gray-700 hover:text-gray-900 select-none font-medium">
          {{ $t('detalles_tecnicos') }}
        </summary>
        <div class="mt-3 space-y-3">
          <div v-if="notification.endpoint">
            <span class="font-semibold">Endpoint:</span>
            <code class="ml-2 px-1.5 py-0.5 bg-gray-100 rounded text-gray-800 break-all">{{ notification.endpoint }}</code>
          </div>

          <div v-if="notification.code !== undefined && notification.code !== null && notification.code !== ''">
            <span class="font-semibold">{{ $t('codigo') }}:</span>
            <code class="ml-2 px-1.5 py-0.5 bg-gray-100 rounded text-gray-800">{{ notification.code }}</code>
          </div>

          <div v-if="notification.response !== undefined && notification.response !== null">
            <div class="flex items-center justify-between mb-1">
              <span class="font-semibold">{{ $t('respuesta') }}</span>
              <button
                type="button"
                class="text-xs text-blue-600 hover:underline"
                @click="copy(notification.response, 'response')"
              >
                {{ copiedKey === 'response' ? $t('copiado') : $t('copiar') }}
              </button>
            </div>
            <pre
              class="bg-gray-100 rounded p-2 text-xs overflow-x-auto whitespace-pre-wrap break-words max-h-48"
            >{{ stringify(notification.response) }}</pre>
          </div>

          <div v-if="notification.payload !== undefined && notification.payload !== null">
            <div class="flex items-center justify-between mb-1">
              <span class="font-semibold">{{ $t('peticion') }}</span>
              <button
                type="button"
                class="text-xs text-blue-600 hover:underline"
                @click="copy(notification.payload, 'payload')"
              >
                {{ copiedKey === 'payload' ? $t('copiado') : $t('copiar') }}
              </button>
            </div>
            <pre
              class="bg-gray-100 rounded p-2 text-xs overflow-x-auto whitespace-pre-wrap break-words max-h-48"
            >{{ stringify(notification.payload) }}</pre>
          </div>
        </div>
      </details>
    </div>

    <!-- Botón de acción -->
    <div class="mt-8 flex justify-end pb-4 px-12">
      <button
        @click="close"
        class="bg-black text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-800 transition-colors min-w-[10rem]"
      >
        {{ $t("cerrar") }}
      </button>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import errorHandler from '@/services/errorHandler';
import Modal from '@/components/theme/ui/Modal.vue';

interface NotificationState {
  type: 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration: number;
  code?: string | number | null;
  response?: unknown;
  payload?: unknown;
  endpoint?: string;
}

interface NotificationPayload extends Partial<NotificationState> {
  status?: number;
}

defineOptions({ name: 'WebServiceErrorToast' });

const router = useRouter();
const errorModal = ref<InstanceType<typeof Modal> | null>(null);
const notification = ref<NotificationState>({
  type: 'error',
  title: '',
  message: '',
  duration: 10000,
});
const timeoutId = ref<ReturnType<typeof setTimeout> | null>(null);
const copiedKey = ref<string | null>(null);

const titleClasses = computed(() => {
  const typeClasses = {
    error: 'text-red-800',
    warning: 'text-yellow-800',
    info: 'text-blue-800',
  };
  return typeClasses[notification.value.type] || typeClasses.error;
});

const messageClasses = computed(() => {
  const typeClasses = {
    error: 'text-red-700',
    warning: 'text-yellow-700',
    info: 'text-blue-700',
  };
  return typeClasses[notification.value.type] || typeClasses.error;
});

const hasDetails = computed(() => {
  const n = notification.value;
  return (
    !!n.endpoint ||
    (n.code !== undefined && n.code !== null && n.code !== '') ||
    (n.response !== undefined && n.response !== null) ||
    (n.payload !== undefined && n.payload !== null)
  );
});

function stringify(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

async function copy(value: unknown, key: string) {
  const text = stringify(value);
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      // Fallback para contextos sin permisos de Clipboard API.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    copiedKey.value = key;
    setTimeout(() => {
      if (copiedKey.value === key) copiedKey.value = null;
    }, 1500);
  } catch {
    // Si el copiado falla no rompemos la UI; el usuario puede seleccionar a mano.
  }
}

function showNotification(payload: NotificationPayload) {
  if (router.currentRoute.value.name === 'dashboard' && (payload.status ?? 0) >= 500) {
    return;
  }

  if (timeoutId.value) clearTimeout(timeoutId.value);

  notification.value = {
    type: payload.type || 'error',
    title: payload.title || 'Error',
    message: payload.message || 'Ha ocurrido un error',
    duration: payload.duration || 10000,
    code: payload.code,
    response: payload.response,
    payload: payload.payload,
    endpoint: payload.endpoint,
  };
  copiedKey.value = null;

  errorModal.value?.show();

  // Con detalles técnicos el usuario suele querer inspeccionar/copiar, así
  // que no auto-cerramos — la dismiss vuelve a ser manual con el botón.
  if (!hasDetails.value) {
    timeoutId.value = setTimeout(() => {
      close();
    }, notification.value.duration);
  }
}

function close() {
  errorModal.value?.close();
  if (timeoutId.value) {
    clearTimeout(timeoutId.value);
    timeoutId.value = null;
  }
}

onMounted(() => {
  errorHandler.setNotificationCallback(showNotification);
});

onBeforeUnmount(() => {
  if (timeoutId.value) clearTimeout(timeoutId.value);
  errorHandler.setNotificationCallback(null);
});
</script>
